import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { platformFromKey, platformHasLanes, platformKeys } from "../api/platforms";
import { useCurrentUser } from "../auth/me";
import { useToast } from "../components/toast";
import { StoresCoverageMap } from "../components/StoresCoverageMap";
import { AnchorKindPill, RolePill, TierPills } from "../components/tiers";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { Badge, Button, Input, PageHeader, Panel, Row, Stack } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Seg, Stat } from "../components/foundation";
import { AnimatedNumber } from "../lib/motion";
import type { Store } from "../api/generated/model";

// Stable empty fallback so `storesQuery.data ?? []` doesn't mint a fresh array
// every render while unresolved/errored (which would loop via onFilteredRowsChange).
const NO_STORES: Store[] = [];
import {
  getLifecycleV1StoresLifecycleGetQueryKey,
  getStoreEventsV1StoreEventsGetQueryKey,
  getStoresV1StoresGetQueryKey,
  useLifecycleV1StoresLifecycleGet,
  useStoreEventsV1StoreEventsGet,
  useStoresV1StoresGet,
  useWipeStoreCacheV1StoresCacheDelete,
  useWipeStoresV1StoresDelete,
} from "../api/generated/qcsControlPlane";
import {
  type Platform,
  ago,
  fmtDateTime,
  fmtInt,
  platformLabel,
  storeStatusLabel,
  storeStatusTone,
} from "../api/labels";

// The store dimension + coverage map are per-platform (a sweep targets one
// platform), so unlike Brands there's no "all": pick a platform.
export function Stores() {
  // ?platform= & ?focus= come from a store page's "Show on store map" link, so the
  // link lands on the right platform's map with that store pinned. Kept in the URL so
  // a reload reproduces the view.
  const [params] = useSearchParams();
  const focusStoreId = params.get("focus");
  const [platform, setPlatform] = useState<Platform>(() => {
    const wanted = platformFromKey(params.get("platform") ?? "");
    return wanted ?? platformKeys()[0];
  });

  const lifecycle = useLifecycleV1StoresLifecycleGet(
    { platform },
    { query: { refetchInterval: 20000 } },
  );
  const storesQuery = useStoresV1StoresGet(
    { platform, limit: 3000 },
    { query: { refetchInterval: 20000 } },
  );
  const eventsQuery = useStoreEventsV1StoreEventsGet(
    { platform, limit: 40 },
    { query: { refetchInterval: 20000 } },
  );

  const counts = lifecycle.data ?? {};
  const all = storesQuery.data ?? NO_STORES;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  type StoreRow = NonNullable<typeof storesQuery.data>[number];

  // Default order is most recently seen first; the table's search / facets /
  // sort / columns work on top (and drive the map via onFilteredRowsChange).
  const data = useMemo<StoreRow[]>(
    () => [...all].sort((a, b) => b.last_seen_at.localeCompare(a.last_seen_at)),
    [all],
  );

  // The table's final visible rows (after the global search) drive the
  // coverage map. `null` (no narrowing at all) tells the map to show everything.
  // Debounced so typing doesn't rebuild map layers on every keystroke.
  const [visibleRows, setVisibleRows] = useState<StoreRow[]>([]);
  const [mapFilterIds, setMapFilterIds] = useState<Set<string> | null>(null);
  useEffect(() => {
    const narrowed = visibleRows.length !== all.length;
    const ids = narrowed ? new Set(visibleRows.map((s) => s.store_id)) : null;
    const t = setTimeout(() => setMapFilterIds(ids), 250);
    return () => clearTimeout(t);
  }, [visibleRows, all.length]);

  const columns = useMemo<AnyColumnDef<StoreRow>[]>(() => {
    const col = createColumnHelper<StoreRow>();
    return [
      col.accessor("store_id", {
        header: "Store",
        cell: ({ row }) => (
          <Link to={`/stores/${platform}/${encodeURIComponent(row.original.store_id)}`}>
            {row.original.store_id}
          </Link>
        ),
        meta: { cellClassName: "mono strong", minWidth: 160 },
      }),
      col.accessor("status", {
        header: "Status",
        cell: ({ row }) => (
          <Badge variant={toneVariant(storeStatusTone(row.original.status))} style={{ fontSize: 10.5 }}>
            {storeStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          width: 120,
          filterSelect: true,
          filterLabel: "status",
          exportValue: (s) => storeStatusLabel(s.status),
        },
      }),
      col.accessor((s) => (s.is_excluded ? "Excluded" : "Counted"), {
        id: "data",
        header: "Data",
        cell: ({ row }) =>
          row.original.is_excluded ? (
            <Badge
              variant="outline"
              style={{ fontSize: 10.5 }}
              title={row.original.excluded_reason || undefined}
            >
              Excluded
            </Badge>
          ) : (
            <span className="faint">—</span>
          ),
        meta: {
          width: 120,
          filterSelect: true,
          filterLabel: "data",
          exportValue: (s) => (s.is_excluded ? s.excluded_reason || "excluded" : ""),
        },
      }),
      col.display({
        id: "tier",
        header: "Tier",
        cell: ({ row }) =>
          platformHasLanes(platform) ? (
            <TierPills tiers={row.original.tiers} fallback={row.original.tier} />
          ) : (
            <RolePill tier={row.original.tier} />
          ),
        meta: { minWidth: 120 },
        enableSorting: false,
      }),
      col.accessor("anchor_kind", {
        header: "Anchor",
        cell: ({ row }) => <AnchorKindPill kind={row.original.anchor_kind} />,
        meta: { width: 130, filterSelect: true, filterLabel: "anchor" },
      }),
      col.accessor((s) => [s.locality, s.city].filter(Boolean).join(", "), {
        id: "location",
        header: "Location",
        cell: ({ row }) => {
          const s = row.original;
          return s.locality || s.city ? (
            <span style={{ fontSize: 12 }}>
              {s.locality && <span className="strong">{s.locality}</span>}
              {s.locality && s.city && <span className="faint">, </span>}
              {s.city && <span className="faint">{s.city}</span>}
            </span>
          ) : (
            <span className="faint">—</span>
          );
        },
        meta: { minWidth: 160 },
      }),
      col.accessor((s) => s.eta_minutes ?? null, {
        id: "eta",
        header: "ETA",
        cell: ({ row }) =>
          row.original.eta_minutes == null ? (
            <span className="faint">—</span>
          ) : (
            `${row.original.eta_minutes}m`
          ),
        meta: { align: "right", width: 90, cellClassName: "num" },
      }),
      col.accessor("latitude", {
        header: "Lat",
        cell: ({ row }) => row.original.latitude.toFixed(4),
        meta: { align: "right", width: 100, cellClassName: "num faint" },
      }),
      col.accessor("longitude", {
        header: "Lon",
        cell: ({ row }) => row.original.longitude.toFixed(4),
        meta: { align: "right", width: 100, cellClassName: "num faint" },
      }),
      col.accessor("missed_sweeps", {
        header: "Missed",
        cell: ({ row }) => fmtInt(row.original.missed_sweeps),
        meta: { align: "right", width: 100, cellClassName: "num" },
      }),
      col.accessor("last_seen_at", {
        header: "Last seen",
        cell: ({ row }) => <span className="faint">{fmtDateTime(row.original.last_seen_at)}</span>,
        meta: { minWidth: 150 },
      }),
    ];
  }, [platform]);

  type EventRow = NonNullable<typeof eventsQuery.data>[number];
  const eventColumns = useMemo<AnyColumnDef<EventRow>[]>(() => {
    const col = createColumnHelper<EventRow>();
    return [
      col.accessor("event_type", {
        header: "Event",
        cell: ({ row }) => (
          <Badge variant={toneVariant(eventTone(row.original.event_type))} style={{ fontSize: 10.5 }}>
            {row.original.event_type}
          </Badge>
        ),
        meta: { width: 110, filterSelect: true, filterLabel: "event" },
      }),
      col.accessor("store_id", {
        header: "Store",
        cell: ({ row }) => (
          <span className="mono" style={{ fontSize: 11 }}>{row.original.store_id}</span>
        ),
        meta: { minWidth: 90 },
      }),
      col.accessor("event_at", {
        header: "When",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 10.5 }}>{ago(row.original.event_at)}</span>
        ),
        meta: { width: 90 },
      }),
    ];
  }, []);
  const events = eventsQuery.data ?? [];

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Stores"
        description="Merchant sweep → dark-store discovery → inventory reads"
        action={
          <Seg
            brand
            value={platform}
            onChange={setPlatform}
            options={platformKeys().map((p) => ({
              value: p,
              label: platformLabel(p),
            }))}
          />
        }
      />

      {/* lifecycle KPIs */}
      <div className="grid g-3 mb-3.5">
        <Stat surface label="total stores" value={<AnimatedNumber value={total} format={fmtInt} />} foot={<span className="faint">current dimension</span>} />
        <Stat surface label="active" value={<AnimatedNumber value={counts.active ?? 0} format={fmtInt} />} tone="ok" foot={<span className="faint">serviceable now</span>} />
        <Stat surface label="closed" value={<AnimatedNumber value={counts.closed ?? 0} format={fmtInt} />} tone="crit" foot={<span className="faint">unseen 2+ weeks</span>} />
      </div>

      {storesQuery.isError && (
        <Panel className="mb-3.5">
          <Panel.Body className="text-crit">
            Could not load stores (is ClickHouse configured + a sweep run?).
          </Panel.Body>
        </Panel>
      )}

      {/* coverage map + lifecycle timeline */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(12,1fr)",
          marginBottom: 14,
          alignItems: "start",
        }}
      >
        <Panel style={{ gridColumn: "span 8" }}>
          <Panel.Head title="Coverage map">
            <div className="flex-1" />
            <span className="faint text-[11px]">all platforms</span>
          </Panel.Head>
          <Panel.Body tight>
            <StoresCoverageMap
              activePlatform={platform}
              filterIds={mapFilterIds}
              focusStoreId={focusStoreId}
            />
            <span className="hint mt-1.5">
              Store logos, clustered. Click any store to pin its serviceable area and the probe
              points where it was found: click more to compare several at once. Other platforms are
              off by default; toggle layers or expand to fullscreen, top-right.
            </span>
          </Panel.Body>
        </Panel>

        <Panel style={{ gridColumn: "span 4" }}>
          <Panel.Head title="Lifecycle timeline">
            <div className="flex-1" />
            <span className="num faint text-[11px]">{(eventsQuery.data ?? []).length} events</span>
          </Panel.Head>
          <Panel.Body tight>
            <DataTable
              tableKey="stores-lifecycle"
              columns={eventColumns}
              data={events}
              getRowId={(e, i) => `${e.store_id}-${e.event_at}-${e.event_type}-${i}`}
              globalPlaceholder="Search store…"
              enableColumnVisibility={false}
              enableCsvExport={false}
              maxBodyHeight={420}
              isLoading={eventsQuery.isLoading}
              emptyMessage="No lifecycle events yet."
            />
          </Panel.Body>
        </Panel>
      </div>

      {/* store table */}
      <Panel>
        <Panel.Head title="Store dimension" />
        <Panel.Body tight>
          <DataTable
            tableKey="stores"
            columns={columns}
            data={data}
            getRowId={(s) => s.store_id}
            globalPlaceholder="Search store id / locality…"
            csvFilename={`stores-${platform}`}
            pinFirstColumn
            defaultHidden={["latitude", "longitude"]}
            onFilteredRowsChange={setVisibleRows}
            // Excluded stores read as set aside, not as a fault: a neutral tint, no
            // semaphore colour, and the text left at full contrast so the row is still
            // readable at a glance.
            rowClassName={(s) => (s.is_excluded ? "row-muted" : undefined)}
            isLoading={storesQuery.isLoading}
            emptyMessage={`No stores: run a merchant sweep for ${platformLabel(platform)}.`}
          />
        </Panel.Body>
      </Panel>

      <DangerZone platform={platform} />
    </>
  );
}

// Superuser-only destructive controls, scoped to the selected platform: wipe all
// discovered-store data (+ cache), or just the serviceability cache. Wiping is
// irreversible, a fresh merchant sweep must rediscover stores from scratch.
function DangerZone({ platform }: { platform: Platform }) {
  const me = useCurrentUser();
  const notify = useToast();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | "stores" | "cache">(null);

  const refresh = () => {
    qc.invalidateQueries({
      queryKey: getStoresV1StoresGetQueryKey({ platform, limit: 3000 }),
    });
    qc.invalidateQueries({
      queryKey: getLifecycleV1StoresLifecycleGetQueryKey({ platform }),
    });
    qc.invalidateQueries({
      queryKey: getStoreEventsV1StoreEventsGetQueryKey({ platform, limit: 40 }),
    });
    qc.invalidateQueries({ queryKey: ["stores-map"] });
  };

  const wipeStores = useWipeStoresV1StoresDelete({
    mutation: {
      onSuccess: () => {
        notify(`Wiped all ${platformLabel(platform)} stores data + cache`);
        setDialog(null);
        refresh();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "Wipe failed", "crit"),
    },
  });
  const wipeCache = useWipeStoreCacheV1StoresCacheDelete({
    mutation: {
      onSuccess: () => {
        notify(`Wiped ${platformLabel(platform)} serviceability cache`);
        setDialog(null);
        refresh();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "Wipe failed", "crit"),
    },
  });

  if (!me.is_superuser) return null;
  const label = platformLabel(platform);
  const busy = wipeStores.isPending || wipeCache.isPending;

  return (
    <>
      <Panel alarm className="mt-3.5">
        <Panel.Head title={<span className="text-crit">Danger zone</span>}>
          <div className="flex-1" />
          <span className="faint text-[11px]">{label} · superuser-only</span>
        </Panel.Head>
        <Panel.Body>
          <Stack gap="lg">
            <DangerRow
              title="Wipe all stores data"
              desc={`Drops the entire ${label} store dimension, sightings, lifecycle events, live coverage and the serviceability cache. A fresh merchant sweep must rediscover every dark store.`}
              action="Wipe stores data"
              busy={busy}
              onClick={() => setDialog("stores")}
            />
            <DangerRow
              title="Wipe cache data"
              desc={`Drops only the ${label} serviceability cache. Stores are kept; the next sweep re-probes every cell live instead of reusing cached verdicts.`}
              action="Wipe cache"
              busy={busy}
              onClick={() => setDialog("cache")}
            />
          </Stack>
        </Panel.Body>
      </Panel>

      {dialog === "stores" && (
        <ConfirmModal
          title="Wipe all stores data"
          confirmLabel="Wipe stores data"
          busy={wipeStores.isPending}
          requireText={platform}
          onClose={() => setDialog(null)}
          onConfirm={() => wipeStores.mutate({ params: { platform } })}
        >
          This permanently deletes <b>all {label} stores data</b>, the store
          dimension, sightings, lifecycle events, live coverage <b>and</b> the
          serviceability cache. A fresh merchant sweep must rediscover every dark
          store from scratch. This cannot be undone.
        </ConfirmModal>
      )}

      {dialog === "cache" && (
        <ConfirmModal
          title="Wipe cache data"
          confirmLabel="Wipe cache"
          busy={wipeCache.isPending}
          onClose={() => setDialog(null)}
          onConfirm={() => wipeCache.mutate({ params: { platform } })}
        >
          This drops only the <b>{label} serviceability cache</b>. Stores are
          kept; the next sweep re-probes every cell live instead of reusing cached
          verdicts. This cannot be undone.
        </ConfirmModal>
      )}
    </>
  );
}

function DangerRow({
  title,
  desc,
  action,
  busy,
  onClick,
}: {
  title: string;
  desc: string;
  action: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <Row gap="lg" align="center">
      <Stack gap="xs">
        <div className="strong" style={{ fontSize: 13 }}>{title}</div>
        <div className="faint" style={{ fontSize: 11.5, maxWidth: 640 }}>{desc}</div>
      </Stack>
      <div className="flex-1" />
      <Button variant="destructive" disabled={busy} onClick={onClick}>{action}</Button>
    </Row>
  );
}

// In-app confirmation popup (replaces the native window.confirm/prompt). For the
// heavier action, pass `requireText`, the confirm button stays disabled until the
// operator types that exact value (e.g. the platform name).
function ConfirmModal({
  title,
  children,
  confirmLabel,
  busy,
  requireText,
  onConfirm,
  onClose,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy: boolean;
  requireText?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const ready = !requireText || typed === requireText;
  return (
    <div className="modal-scrim" onClick={busy ? undefined : onClose}>
      <Panel
        alarm
        style={{ width: "100%", maxWidth: 460 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Panel.Head title={<span className="text-crit">{title}</span>}>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} disabled={busy} aria-label="close">
            ✕
          </Button>
        </Panel.Head>
        <Panel.Body>
          <Stack gap="lg">
            <div className="faint" style={{ fontSize: 12.5, lineHeight: 1.55 }}>{children}</div>
            {requireText && (
              <label className="field">
                <span className="hint">
                  Type <b className="mono" style={{ color: "var(--ink-1)" }}>{requireText}</b> to confirm
                </span>
                <Input
                  className="mono"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={requireText}
                  autoFocus
                  disabled={busy}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && ready && !busy) onConfirm();
                  }}
                />
              </label>
            )}
            <Row gap="sm" justify="end">
              <Button variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={onConfirm} disabled={!ready || busy}>
                {busy ? "Wiping…" : confirmLabel}
              </Button>
            </Row>
          </Stack>
        </Panel.Body>
      </Panel>
    </div>
  );
}

function eventTone(t: string): "ok" | "warn" | "info" | "idle" {
  if (t === "added") return "ok";
  if (t === "reactivated") return "info";
  if (t === "removed") return "warn";
  return "idle";
}
